import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "../lib/api";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { Button } from "../components/ui/button";
import { Skeleton } from "../components/ui/skeleton";
import { BookOpen, ArrowRight, Check, ArrowLeft } from "lucide-react";

export function StudyPathsList() {
  const [paths, setPaths] = useState(null);
  useEffect(() => { api.get("/study-paths").then((r) => setPaths(r.data.items)); }, []);
  return (
    <div className="space-y-8" data-testid="study-paths">
      <div>
        <div className="label-caps mb-2">Guided curricula</div>
        <h1 className="text-3xl sm:text-5xl tracking-tight font-semibold">Study Paths.</h1>
        <p className="text-muted-foreground mt-3 max-w-2xl">
          Each path is a sequenced reading plan drawn from the tribunal library — role-specific or theme-specific.
        </p>
      </div>
      {paths === null ? (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-44" />)}
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {paths.map((p) => (
            <Link key={p.slug} to={`/app/paths/${p.slug}`} data-testid={`path-${p.slug}`}>
              <Card className="p-6 h-full hover:border-primary/50 transition-colors duration-200">
                <div className="flex items-center justify-between mb-3">
                  <BookOpen className="h-5 w-5 text-primary" />
                  <Badge variant="secondary" className="font-mono text-[10px]">{p.duration_weeks} wk</Badge>
                </div>
                <div className="text-lg font-medium tracking-tight">{p.name}</div>
                <div className="text-xs text-muted-foreground mt-1">For {p.role}</div>
                <p className="text-sm text-muted-foreground mt-3 line-clamp-3">{p.description}</p>
                <div className="mt-4 label-caps flex items-center gap-1">
                  {p.chapter_count} chapters <ArrowRight className="h-3 w-3" />
                </div>
              </Card>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

export function StudyPathDetail() {
  const { slug } = useParams();
  const [data, setData] = useState(null);

  const load = async () => {
    const { data } = await api.get(`/study-paths/${slug}`);
    setData(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [slug]);

  const toggle = async (ch_id) => {
    await api.post(`/study-paths/${slug}/complete/${ch_id}`);
    load();
  };

  if (!data) return <Skeleton className="h-96" />;
  const { path, progress } = data;
  const completed = new Set(progress?.completed || []);
  const pct = path.chapters.length ? Math.round((completed.size / path.chapters.length) * 100) : 0;

  return (
    <div className="space-y-8 max-w-4xl" data-testid="study-path-detail">
      <Link to="/app/paths" className="label-caps flex items-center gap-1 text-muted-foreground hover:text-foreground">
        <ArrowLeft className="h-3 w-3" /> All paths
      </Link>
      <div>
        <div className="label-caps mb-2">Path · {path.pillar}</div>
        <h1 className="text-3xl md:text-5xl tracking-tighter font-semibold">{path.name}</h1>
        <p className="text-muted-foreground mt-3 max-w-2xl">{path.description}</p>
        <div className="mt-6">
          <div className="flex items-center justify-between text-xs text-muted-foreground mb-2">
            <span>{completed.size} of {path.chapters.length} complete</span>
            <span className="font-mono">{pct}%</span>
          </div>
          <Progress value={pct} className="h-1" />
        </div>
      </div>

      <div className="space-y-2">
        {path.chapters.map((c, i) => {
          const done = completed.has(c.id);
          return (
            <div key={c.id} data-testid={`path-ch-${c.id}`}
              className="flex items-start gap-3 p-4 rounded-lg border border-border hover:border-primary/50 transition-colors duration-200">
              <button onClick={() => toggle(c.id)}
                className={`shrink-0 h-6 w-6 rounded-full border-2 flex items-center justify-center transition-colors ${
                  done ? "bg-primary border-primary text-primary-foreground" : "border-border hover:border-primary"
                }`}>
                {done && <Check className="h-3 w-3" />}
              </button>
              <Link to={`/app/learn/chapter/${c.id}`} className="flex-1 min-w-0">
                <div className="font-mono text-[10px] text-muted-foreground">Week {Math.floor(i / Math.max(1, Math.ceil(path.chapters.length / path.duration_weeks))) + 1}</div>
                <div className="text-sm font-medium mt-0.5">{c.title}</div>
                <div className="text-xs text-muted-foreground mt-0.5">{c.topic}</div>
              </Link>
              <ArrowRight className="h-4 w-4 text-muted-foreground shrink-0 mt-1" />
            </div>
          );
        })}
      </div>
    </div>
  );
}
